import { useCallback } from 'react';
import { eventFormSubmissionApi } from '../api/eventFormSubmissions';
import type { FormState, Question } from '../types/publicForm.types';

interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

export const useEventFormValidation = (eventSlug: string, formSlug: string, formState: FormState) => {
  const validatePage = useCallback((questions: Question[]): ValidationResult => {
    const errors: Record<string, string> = {};
    
    questions.forEach(question => {
      // Skip validation for hidden child questions
      if (question.parentQuestionId && !formState.visibleChildQuestions.has(question.id)) {
        return;
      }
      
      const value = formState.formData[question.id];
      const commentValue = formState.formData[`${question.id}_comment`];
      
      // Check required fields
      if (question.required) {
        if (value === undefined || value === null || value === '' || 
            (Array.isArray(value) && value.length === 0)) {
          errors[question.id] = `${question.questionPrompt || question.name} is required`;
        }
      }
      
      // Check required comments
      if (question.showComments && question.commentsRequired && !commentValue) {
        errors[`${question.id}_comment`] = 'Comment is required';
      }
      
      // Additional validation based on answer style
      if (value !== undefined && value !== null && value !== '') {
        switch (question.answerStyle) {
          case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(String(value))) {
              errors[question.id] = 'Please enter a valid email address';
            }
            break;
            
          case 'url':
            try {
              new URL(String(value));
            } catch {
              errors[question.id] = 'Please enter a valid URL';
            }
            break;
            
          case 'integer':
            if (!Number.isInteger(Number(value))) {
              errors[question.id] = 'Please enter a whole number';
            }
            break;
            
          case 'decimal':
            if (isNaN(Number(value))) {
              errors[question.id] = 'Please enter a valid number';
            }
            break;
        }
      }
    });
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }, [formState.formData, formState.visibleChildQuestions]);

  const validatePageWithServer = useCallback(async (
    pageIndex: number,
    responses: any
  ): Promise<ValidationResult> => {
    try {
      const result = await eventFormSubmissionApi.validatePage(
        eventSlug,
        formSlug,
        pageIndex,
        responses
      );
      
      return {
        isValid: result.valid,
        errors: result.errors || {},
      };
    } catch (error) {
      console.error('Server validation error:', error);
      return {
        isValid: false,
        errors: { base: 'Validation failed. Please try again.' },
      };
    }
  }, [eventSlug, formSlug]);

  const validateField = useCallback(async (
    questionId: string,
    value: any
  ): Promise<{ valid: boolean; error?: string }> => {
    try {
      return await eventFormSubmissionApi.validateField(
        eventSlug,
        formSlug,
        questionId,
        value
      );
    } catch (error) {
      console.error('Field validation error:', error);
      return { valid: false, error: 'Validation failed' };
    }
  }, [eventSlug, formSlug]);

  return {
    validatePage,
    validatePageWithServer,
    validateField,
  };
};